// DataTable.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Box,
  useTheme,
  TableSortLabel,
  Dialog,
  DialogContent,
  CircularProgress,
  Alert
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import { alpha } from '@mui/material/styles';
import { formatText } from '../../common/TextFormatterProvider';
import { formatBooleanValue } from '../../../utils/tagValueUtils';
import { downloadFromEndpoint } from '../../../services/api_utils';
import { functionEndpoint } from '../../../services/endpoints';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import {
  isAnyPeriodColumn,
  formatPeriodKey,
  sortPeriodColumns,
} from '../../../utils/periodUtils';
import CellLineageDrawer from './CellLineageDrawer';
import useVirtualWindow from '../../../hooks/useVirtualWindow';
import useColumnWidths from '../../../hooks/useColumnWidths';
import { ColumnWidthGroup, gridBodyStyles, gridHeaderCellStyles } from '../../common/tableRendering';
import { CONTRIBUTOR_MARKER_FIELD } from '../../../utils/contributorMarker';
import { isExternalTable } from '../../../utils/tableTypes';
import {
  buildTableColumnTypeMap,
  getRawCellValue,
  getRawCellValueByPath,
  getTableLeafType,
  toExcelCellValue,
  applyColumnWidths,
  formatDeclaredDate,
  isDateType,
} from '../../../utils/excelExport';
import { countNoun } from '../../../utils/pluralise';

const DELIMITER = '§';

// Cells a single totals chunk may sum before yielding to the browser. Sized so an
// ordinary grid finishes in the first frame — exactly as the old synchronous pass
// did — while a 50,000-row, 400-column one paints its columns progressively instead
// of freezing the tab for several seconds.
const CELLS_PER_TOTALS_CHUNK = 250000;

// The sort arrow beside a header label: an 18px icon with a 4px margin each side. Every
// header's width keeps room for one: most headers have one, and those that do not
// (labels over a group of columns) lose only that margin.
const SORT_ICON_WIDTH = 26;

// A header label as drawn, for measuring its width: detail and attribute headers are
// drawn with `textTransform: 'capitalize'`.
const headerTextAsDrawn = (header) => (header.isStatic
  ? header.label.replace(/(^|\s)(\S)/g, (match, space, letter) => space + letter.toUpperCase())
  : header.label);

// A value rewritten with every map's keys in a stable order, at every depth, so that
// JSON.stringify of two schemas equal BY VALUE produces the same string. Firestore
// does not promise key order, and without this an equivalent document arriving with
// display_order's properties, or a metric's {type, updateBehavior}, in a different
// order rebuilt the headers, rescanned every row and restarted the totals.
// Arrays keep their order — in display_order the order IS the value.
const canonical = (value) => {
  if (Array.isArray(value)) return value.map(canonical);
  if (value === null || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([key, nested]) => [key, canonical(nested)]),
  );
};

// Cells the browser will serialise into a workbook itself. Past this the export is
// built on the server: SheetJS allocates an object per cell and then the whole XML
// on top of that, which at the Inspect row cap froze the tab for minutes or had the
// renderer killed outright — no file, and no error the page could catch.
const MAX_IN_BROWSER_EXPORT_CELLS = 100000;
const numberFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatMonthKey = (column) => {
  // Use the new period formatting utility which handles all period types
  // (day, month, quarter, year)
  if (typeof column === 'string' && isAnyPeriodColumn(column)) {
    return formatPeriodKey(column);
  }
  return formatText(column); // Use general formatter for other potential top-level keys
};

const formatNumber = (value) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return numberFormatter.format(value);
  }
  return value;
};

// Analyzes the structure of nested columns across the entire dataset
function analyzeColumns(data) {
  if (!Array.isArray(data) || data.length === 0) {
    return { columns: {}, maxDepth: 0, dynamicColOrder: [] };
  }

  const columnInfo = {};
  const dynamicColOrder = new Set(); // To maintain order of dynamic top-level columns

  // Collect info for each top-level key across all rows
  data.forEach(row => {
    Object.keys(row).forEach(col => {
      // Dynamic columns are period columns (day, month, quarter, year formats)
      const isDynamic = isAnyPeriodColumn(col);
      if (!isDynamic) return; // Skip static columns for this analysis

      dynamicColOrder.add(col); // Add to ordered set

      if (!columnInfo[col]) {
        columnInfo[col] = { depth: 0, singleLevelKeys: new Set(), doubleLevelKeys: {} };
      }

      const val = row[col];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        Object.keys(val).forEach(subKey => {
          const subVal = val[subKey];
          if (subVal && typeof subVal === 'object' && !Array.isArray(subVal)) {
            // Double-level nesting
            columnInfo[col].depth = Math.max(columnInfo[col].depth, 2);
            if (!columnInfo[col].doubleLevelKeys[subKey]) {
              columnInfo[col].doubleLevelKeys[subKey] = new Set();
            }
            Object.keys(subVal).forEach(leafKey => columnInfo[col].doubleLevelKeys[subKey].add(leafKey));
          } else {
            // Single-level nesting (or potentially a leaf node if no deeper nesting found elsewhere)
            if (columnInfo[col].depth < 2) { // Only mark as depth 1 if not already depth 2
               columnInfo[col].depth = Math.max(columnInfo[col].depth, 1);
            }
            columnInfo[col].singleLevelKeys.add(subKey);
          }
        });
      } else {
         // Non-object value under the dynamic key, treat as depth 0 for this column *unless* other rows make it nested
         columnInfo[col].depth = Math.max(columnInfo[col].depth, 0);
      }
    });
  });

  // Determine global max depth *among dynamic columns*
  let maxDepth = 0;
  Object.values(columnInfo).forEach(info => {
    maxDepth = Math.max(maxDepth, info.depth);
  });

   // Sort dynamic columns chronologically using period-aware sorting
   const sortedDynamicCols = sortPeriodColumns(Array.from(dynamicColOrder));

  return { columns: columnInfo, maxDepth, dynamicColOrder: sortedDynamicCols };
}

// Utility: given an array and an order list, return items reordered according to order, with
// items not present in order appended in original sequence
function applyOrderWithFallback(items, orderList) {
  if (!Array.isArray(items)) return [];
  if (!Array.isArray(orderList) || orderList.length === 0) return items;
  const set = new Set(items);
  const ordered = [];
  orderList.forEach(key => {
    if (set.has(key)) {
      ordered.push(key);
      set.delete(key);
    }
  });
  // Append remaining in original item order
  items.forEach(key => {
    if (set.has(key)) ordered.push(key);
  });
  return ordered;
}

// Builds the multi-level header structure
// The default order of metric columns wherever no custom order is saved:
// alphabetical, numeric-aware and case-insensitive — the order the Settings
// page shows under "Use default order", so the two surfaces agree.
const DEFAULT_METRIC_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function buildHeaderStructure(data, tableSchema) {
  const { columns: dynamicColumnInfo, maxDepth, dynamicColOrder } = analyzeColumns(data);
  const { detailColumns = [], attributeColumns = [], tableAttributeColumns = [], identifier = {}, tableType } = tableSchema || {};
  // const detailColumns = detailColumns; // Alias for minimal code changes in this function (Removed duplicate declaration)
  const displayOrder = tableSchema?.display_order || null;
  const displayColumns = tableSchema?.display_columns || null;

  // Determine whether a dynamic leaf (a metric inside a time-series period
  // column) should be visible based on display_columns map
  const isLeafVisible = (parts) => {
    if (!displayColumns) return true;
    // 'period' prefix (works for all period types)
    const periodKey = `period.${parts.join('.')}`;

    if (Object.prototype.hasOwnProperty.call(displayColumns, periodKey)) {
      return !!displayColumns[periodKey];
    }
    return true;
  };

  const headerRows = { top: [], middle: [], bottom: [] };
  const leafOrder = []; // Stores the final flattened IDs for data rendering order

  // --- 1. Static Columns ---
  // Combine all static-like columns, respecting display_columns if needed
  const allStaticSchemaCols = [identifier, ...attributeColumns, ...tableAttributeColumns, ...detailColumns]
    .filter(col => col && col.name) // Ensure valid column definitions
    .filter(col => {
      // Basic filtering: If display_columns is defined, check the top-level name
      // Note: This doesn't handle hiding *nested* dynamic fields via display_columns yet.
      if (tableSchema?.display_columns && tableSchema.display_columns[col.name] === false) {
        return false;
      }
      return true;
    });

  // Order static-like columns as: identifier first (fixed), then metadata per order, then static per order
  const rowIdName = identifier?.name;
  const metadataNames = [...attributeColumns, ...tableAttributeColumns].map(c => c?.name).filter(Boolean);
  const staticNames = detailColumns.map(c => c?.name).filter(Boolean);
  const orderedMetadata = applyOrderWithFallback(metadataNames, displayOrder?.attribute || []);
  const orderedStatic = applyOrderWithFallback(staticNames, displayOrder?.detail || []);
  const orderedStaticLike = [rowIdName, ...orderedMetadata, ...orderedStatic].filter(Boolean);

  // Determine rowSpan for static columns based on dynamic maxDepth
  const staticRowSpan = maxDepth === 2 ? 3 : (maxDepth === 1 ? 2 : 1);

  // Render static-like columns following orderedStaticLike but respecting visibility filter above
  const staticLikeMap = new Map(allStaticSchemaCols.map(c => [c.name, c]));
  orderedStaticLike.forEach(name => {
    const col = staticLikeMap.get(name);
    if (!col) return;
    const header = {
      id: col.name,
      label: formatText(col.name), // Use formatText for consistency
      isStatic: true,
      isSticky: (col.name === identifier?.name),
      rowSpan: staticRowSpan,
      colSpan: 1,
      depth: 0 // Details are always depth 0
    };
    headerRows.top.push(header);
    leafOrder.push(header.id); // Add static column ID to leaf order
  });

  // --- 2. Dynamic/Metrics Columns ---
  
  if (tableType === 'raw') {
    // A raw table has no metrics and no periods: its columns are the static
    // columns above.
    return { headerRows, maxDepth: 0, leafOrder };
  }

  // Helper to get ordered keys from Set/Object properties, maintaining order found in data
    const getOrderedKeys = (topCol, level1Key = null) => {
        const keys = new Set();
        data.forEach(row => {
            let targetObj = row[topCol];
            if (level1Key && targetObj && typeof targetObj === 'object') {
                targetObj = targetObj[level1Key];
            }
            if (targetObj && typeof targetObj === 'object' && !Array.isArray(targetObj)) {
                Object.keys(targetObj).forEach(key => keys.add(key));
            }
        });
        // Basic sort, could be customized if needed (e.g., specific order for 'Commissions', 'Premiums')
        return Array.from(keys).sort(DEFAULT_METRIC_COLLATOR.compare);
    };


  // For dynamic columns, apply display_order.metric within each period.
  const dynamicLeafOrder = Array.isArray(displayOrder?.metric) ? displayOrder.metric : null;

  dynamicColOrder.forEach(col => {
    const info = dynamicColumnInfo[col] || { depth: 0, singleLevelKeys: new Set(), doubleLevelKeys: {} };
    const { depth } = info;
    const formattedColLabel = formatMonthKey(col); // Format 'YYYY_MM' nicely

    if (maxDepth === 2) { // Max 3 header rows needed
      if (depth === 0) {
        // Dynamic column, but no nesting found across all rows
        headerRows.top.push({ id: col, label: formattedColLabel, colSpan: 1, rowSpan: 3, depth: 0 });
        leafOrder.push(col);
      } else if (depth === 1) {
        // Single-level nesting (e.g., { month: { key1: val, key2: val } })
        let singleKeys = getOrderedKeys(col);
        if (dynamicLeafOrder) {
          // Reorder keys per dynamic order list; names map: schema uses keys like 'Gross_Premium' or 'A.B'
          singleKeys = applyOrderWithFallback(singleKeys, dynamicLeafOrder);
        }
        // Apply visibility filter from display_columns
        singleKeys = singleKeys.filter(key => isLeafVisible([key]));
        if ((singleKeys.length || 0) === 0) {
          return; // Skip this month if no visible leaves
        }
        headerRows.top.push({ id: col, label: formattedColLabel, colSpan: singleKeys.length || 1, rowSpan: 2, depth: 1 });
        // Middle row is skipped
        singleKeys.forEach(key => {
          const leafId = `${col}${DELIMITER}${key}`;
          headerRows.bottom.push({ id: leafId, label: formatText(key) });
          leafOrder.push(leafId);
        });
         if (singleKeys.length === 0) { // Handle case where object exists but is empty
             headerRows.bottom.push({ id: col, label: '' }); // Placeholder bottom cell
             leafOrder.push(col); // Use top-level ID if no leaves
         }
      } else { // depth === 2
        // Double-level nesting (e.g., { month: { cat1: { leafA: val }, cat2: { leafB: val } } })
        let level1Keys = getOrderedKeys(col);
        if (dynamicLeafOrder) {
          // Order by first segment occurrence in dynamic order
          const indexOf = (k) => {
            const idx = dynamicLeafOrder.findIndex(x => (x.split('.')[0] === k));
            return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
          };
          level1Keys = [...level1Keys].sort((a, b) => indexOf(a) - indexOf(b));
        }
        // Build visible structure and compute spans with visibility applied
        const visibleBranches = [];
        let totalLeafCount = 0;
        level1Keys.forEach(l1Key => {
          const isGroup = Object.prototype.hasOwnProperty.call(info.doubleLevelKeys, l1Key);
          if (!isGroup) {
            if (isLeafVisible([l1Key])) {
              visibleBranches.push({ l1Key, level2Keys: [], isGroup: false });
              totalLeafCount += 1;
            }
            return;
          }

          let l2 = getOrderedKeys(col, l1Key);
          if (dynamicLeafOrder) {
            // Order leaves as per entries like 'Parent.Child'
            const filtered = dynamicLeafOrder
              .filter(x => x.startsWith(`${l1Key}.`))
              .map(x => x.split('.')[1]);
            l2 = applyOrderWithFallback(l2, filtered);
          }
          // Apply visibility filter
          const visibleL2 = l2.filter(l2Key => isLeafVisible([l1Key, l2Key]));
          if (visibleL2.length > 0) {
            visibleBranches.push({ l1Key, level2Keys: visibleL2, isGroup: true });
            totalLeafCount += visibleL2.length;
          }
        });

        if (totalLeafCount === 0) {
          return; // Skip this month entirely if no visible leaves
        }

        headerRows.top.push({ id: col, label: formattedColLabel, colSpan: totalLeafCount, rowSpan: 1, depth: 2 });

        visibleBranches.forEach(({ l1Key, level2Keys, isGroup }) => {
          const branchId = `${col}${DELIMITER}${l1Key}`;
          if (!isGroup) {
            // A metric that stands alone beside grouped ones has no row of its own beneath
            // it: its cell spans the middle and bottom rows. It is a column of values like
            // any bottom-row cell, so it carries the same sort control (`isLeaf`).
            headerRows.middle.push({ id: branchId, label: formatText(l1Key), colSpan: 1, rowSpan: 2, isLeaf: true });
            leafOrder.push(branchId);
            return;
          }

          headerRows.middle.push({ id: branchId, label: formatText(l1Key), colSpan: level2Keys.length, rowSpan: 1 });
          level2Keys.forEach(l2Key => {
            const leafId = `${col}${DELIMITER}${l1Key}${DELIMITER}${l2Key}`;
            headerRows.bottom.push({ id: leafId, label: formatText(l2Key) });
            leafOrder.push(leafId);
          });
        });
      }
    } else if (maxDepth === 1) { // Max 2 header rows needed
      if (depth === 0) {
        headerRows.top.push({ id: col, label: formattedColLabel, colSpan: 1, rowSpan: 2, depth: 0 });
        leafOrder.push(col);
      } else { // depth 1 or 2 (treat depth 2 as depth 1 if maxDepth is 1)
        let singleKeys = getOrderedKeys(col);
        if (dynamicLeafOrder) {
          singleKeys = applyOrderWithFallback(singleKeys, dynamicLeafOrder);
        }
        // Apply visibility filter from display_columns
        singleKeys = singleKeys.filter(key => isLeafVisible([key]));
        if ((singleKeys.length || 0) === 0) {
          return;
        }
        headerRows.top.push({ id: col, label: formattedColLabel, colSpan: singleKeys.length || 1, rowSpan: 1, depth: 1 });
        singleKeys.forEach(key => {
          const leafId = `${col}${DELIMITER}${key}`;
          headerRows.bottom.push({ id: leafId, label: formatText(key) });
          leafOrder.push(leafId);
        });
        if (singleKeys.length === 0) {
             headerRows.bottom.push({ id: col, label: '' });
             leafOrder.push(col);
         }
      }
    } else { // maxDepth === 0 (Only 1 header row)
      headerRows.top.push({ id: col, label: formattedColLabel, colSpan: 1, rowSpan: 1, depth: 0 });
      leafOrder.push(col);
    }
  });

  return { headerRows, maxDepth, leafOrder };
}

// Retrieves the final value from a potentially nested row object.
// `dateLeafSet` holds the leafIds whose DECLARED schema type is Date — the only
// cells rendered as calendar days. A Text value that merely looks like a date
// stays exactly as stored (M66).
function getCellValue(row, columnId, dateLeafSet = null) {
  const parts = columnId.split(DELIMITER);
  let value = row;
  try {
    for (const p of parts) {
      if (value && typeof value === 'object' && p in value) {
        value = value[p];
      } else {
        // If intermediate path doesn't exist, or final part doesn't exist
        return null; // Return null to render as '-' later
      }
    }

    // Value found, apply formatting
    if (value === null || value === undefined || value === '') return null; // Treat empty strings like null for display consistency

    // Dates are formatted by DECLARATION, never by value shape.
    if (dateLeafSet && dateLeafSet.has(columnId)) {
      const formattedDate = formatDeclaredDate(value);
      if (formattedDate !== value) return formattedDate;
    }

    const formattedNum = formatNumber(value);
     // Check if formatting actually changed it (to avoid formatting strings like '123')
     // And ensure the original wasn't already a string representation of the formatted number
    if (typeof value === 'number' && formattedNum !== value && String(value) !== formattedNum) {
        return formattedNum; // Return if number formatting applied
    }


    // Return raw value if no other formatting applied (could be string, boolean, etc.)
    return value;
  } catch (error) {
    console.error("Error getting cell value for:", columnId, "in row:", row, error);
    return null; // Return null on error
  }
}

// One data row, memoised so a window shift only renders the rows that actually
// entered the window. Every prop of a retained row is referentially stable
// while scrolling — globalIndex is the row's position in the sorted data, so
// even the stripe parity survives the shift — which lets React.memo skip the
// other ~100 rows.
//
// The row and its cells carry the class names that gridBodyStyles (tableRendering.js)
// styles once for the whole body. Rows stripe by globalIndex, not by DOM position,
// which shifts with the scroll window and its spacer row.
const DataTableRow = React.memo(function DataTableRow({
  row,
  globalIndex,
  rowRef,
  leafOrder,
  stickyId,
  staticLeafSet,
  numberLeafSet,
  lineageLeafSet,
  onCellClick,
  getCell,
}) {
  return (
    <TableRow
      ref={rowRef}
      hover
      className={globalIndex % 2 === 0 ? 'body-row striped-row' : 'body-row'}
    >
      {leafOrder.map((leafId) => {
        const isStickyCol = stickyId === leafId;
        const isStaticCol = staticLeafSet.has(leafId);
        const isClickable = lineageLeafSet.has(leafId);
        const text = getCell(row, leafId);

        const classNames = ['data-cell'];
        // Static columns read left to right; metrics and Number-typed columns align right.
        if (!isStaticCol || numberLeafSet.has(leafId)) classNames.push('align-right');
        if (isStickyCol) {
          classNames.push(globalIndex % 2 === 0 ? 'identifier-cell' : 'identifier-cell identifier-cell-alternate');
        }
        if (isClickable) classNames.push('lineage-cell');

        return (
          <td
            key={`${row._internalIndex}-${leafId}`}
            className={classNames.join(' ')}
            onClick={isClickable ? () => onCellClick(row, leafId) : undefined}
            title={text} // The full value on hover, for text cut off by the column width
          >
            {text}
          </td>
        );
      })}
    </TableRow>
  );
});

// --- DataTable Component ---

// `fetched` is whether the rows on offer are the answer to a fetch — a table
// that has not been queried yet has no rows to show, not an empty result.
function DataTable({ data, tableSchema, bindExpandControls, exportHeaders, exportContext, companyId, tableId, loading, fetched = true, forwardFilledFrom }) {
  const [orderBy, setOrderBy] = useState(null); // leafId or static column id
  const [order, setOrder] = useState('asc'); // 'asc' | 'desc'
  const [isExpanded, setIsExpanded] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  // Saved export formats for the Download menu: { status: 'loading' | 'ready' |
  // 'error', list }. The grid outlives a table switch, so the list is cleared
  // the moment the table changes and a response for a previous table is dropped.
  const [exportMappings, setExportMappings] = useState({ status: 'loading', list: [] });
  const [exporting, setExporting] = useState(false);
  // Why the last export produced no file, shown beside the Download button
  // until the next export starts.
  const [exportError, setExportError] = useState('');
  // A successful export that differs from what is on screen still has something to
  // say, and saying it in the error colour reads as a failure.
  const [exportNotice, setExportNotice] = useState('');

  // The export endpoint answers a refusal with a sentence the user can act on ("too
  // large — export a shorter date range"). A transport failure has no such sentence,
  // only the browser's own "Failed to fetch", so it keeps the generic advice.
  const serverGuidanceOr = (error, fallback) => {
    const message = String(error?.message || '').trim();
    const isTransportFailure = error instanceof TypeError || /^Failed to fetch$/i.test(message);
    return (message && !isTransportFailure) ? message : fallback;
  };
  const [selectedCell, setSelectedCell] = useState(null); // For cell lineage drawer
  // The drawer shows the clicked cell's value as the grid read it. New rows are
  // a new read, so an open drawer closes.
  useEffect(() => {
    setSelectedCell(null);
  }, [data]);
  const theme = useTheme();
  // Expose expand controls to parent if requested. The controls object is
  // built once: the parent stores it in state, so handing over a fresh object
  // on every run would re-render the parent, which would re-run this effect
  // if its binder is not referentially stable — an unbounded render loop.
  const expandControls = useMemo(() => ({
    open: () => setIsExpanded(true),
    close: () => setIsExpanded(false)
  }), []);
  useEffect(() => {
    if (typeof bindExpandControls === 'function') {
      bindExpandControls(expandControls);
    }
  }, [bindExpandControls, expandControls]);

  // Load export mappings when exportContext changes
  useEffect(() => {
    let cancelled = false;
    if (!exportContext?.companyId || !exportContext?.tableId) {
      // No table to read for: nothing saved, and nothing pending.
      setExportMappings({ status: 'ready', list: [] });
      return undefined;
    }
    setExportMappings({ status: 'loading', list: [] });
    async function loadMappings() {
      try {
        const q = collection(db, `users/${exportContext.companyId}/tables/${exportContext.tableId}/export_formats`);
        const snap = await getDocs(q);
        if (cancelled) return;
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        setExportMappings({ status: 'ready', list });
      } catch (e) {
        if (cancelled) return;
        // The SDK has already retried transport failures; a rejection is
        // permanent for now. The default export stays available.
        console.error('Failed to load saved export formats:', e);
        setExportMappings({ status: 'error', list: [] });
      }
    }
    loadMappings();
    return () => { cancelled = true; };
  }, [exportContext?.companyId, exportContext?.tableId]);

  const naturalCollator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }), []);

  // Add a unique index to each row for stable keys if no better identifier exists
  const processedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((row, index) => ({ ...row, _internalIndex: index }));
  }, [data]);

  // The tables list is live, so `tableSchema` is a NEW OBJECT on every write to any
  // table document — the backend learning one possible_value on an unrelated table
  // replaces every table object in the page's state. Everything below that walks the
  // rows is keyed on this signature instead of on that identity, so it recomputes only
  // when the grid's shape actually changes. DynamicFilters guards itself the same way
  // and its comment names the same trigger.
  // Map fields are sorted so that two schemas equal BY VALUE produce the same string:
  // Firestore does not promise key order, and an equivalent document arriving with
  // display_columns in a different order would otherwise rebuild the headers, rescan
  // every row and restart the totals for no change at all.
  const gridSchemaSignature = useMemo(() => JSON.stringify(canonical({
    identifier: tableSchema?.identifier ?? null,
    detailColumns: tableSchema?.detailColumns ?? [],
    attributeColumns: tableSchema?.attributeColumns ?? [],
    tableAttributeColumns: tableSchema?.tableAttributeColumns ?? [],
    metricColumns: { schema: tableSchema?.metricColumns?.schema ?? {} },
    display_columns: tableSchema?.display_columns ?? {},
    display_order: tableSchema?.display_order ?? {},
    tableType: tableSchema?.tableType ?? null,
    timeSeriesConfig: tableSchema?.timeSeriesConfig ?? null,
    // Resolved here rather than carried raw, so the signature holds a plain boolean
    // and the absent-means-on default lives in exactly one place.
    show_column_totals: (tableSchema && Object.prototype.hasOwnProperty.call(tableSchema, 'show_column_totals'))
      ? !!tableSchema.show_column_totals
      : true,
  })), [tableSchema]);
  const gridSchema = useMemo(() => JSON.parse(gridSchemaSignature), [gridSchemaSignature]);
  // A boolean, not the object: the header build below walks every row, so it must not
  // be invalidated by the schema object's identity changing under it.
  const hasTableSchema = Boolean(tableSchema);

  const tableColumnTypeMap = useMemo(
    () => buildTableColumnTypeMap(gridSchema),
    [gridSchema],
  );

  // Build header structure and leaf order using the new logic
  const { headerRows, maxDepth, leafOrder } = useMemo(() => {
    if (!processedData || !hasTableSchema) {
      return { headerRows: { top: [], middle: [], bottom: [] }, maxDepth: 0, leafOrder: [] };
    }
    return buildHeaderStructure(processedData, gridSchema);
  }, [processedData, gridSchema, hasTableSchema]);

  // Precompute sticky column ID and static column set for O(1) lookups
  const stickyId = tableSchema?.identifier?.name;
  const tableType = tableSchema?.tableType;
  const lineageEnabled = Boolean(companyId && tableId) && !isExternalTable(tableSchema);
  const staticLeafSet = useMemo(
    () => new Set(headerRows.top.filter(h => h.isStatic).map(h => h.id)),
    [headerRows.top]
  );
  // The cells a click explains in the lineage drawer. On a raw table every cell,
  // by its row's source units. On a time-series table the metric, detail and
  // attribute cells, each of which the files state; never the identifier, which
  // names the row, or a table attribute, one constant of the table that no file
  // states (Spec 6 section 9).
  const lineageLeafSet = useMemo(() => {
    if (!lineageEnabled) return new Set();
    if (tableType === 'raw') return new Set(leafOrder);
    const unexplainedColumns = new Set([
      gridSchema.identifier?.name,
      ...gridSchema.tableAttributeColumns.map((column) => column.name),
    ]);
    return new Set(leafOrder.filter((leafId) => !unexplainedColumns.has(leafId)));
  }, [lineageEnabled, tableType, leafOrder, gridSchema]);

  // Leafs whose declared schema type is Number — right-aligned regardless of
  // which column group they belong to.
  const numberLeafSet = useMemo(() => {
    const set = new Set();
    leafOrder.forEach((leafId) => {
      const declared = getTableLeafType(gridSchema, tableColumnTypeMap, leafId, DELIMITER);
      if (String(declared || '').trim().toLowerCase() === 'number') set.add(leafId);
    });
    return set;
  }, [leafOrder, gridSchema, tableColumnTypeMap]);

  // Leafs whose declared schema type is Date — the only cells getCellValue
  // renders as calendar days (M66).
  const dateLeafSet = useMemo(() => {
    const set = new Set();
    leafOrder.forEach((leafId) => {
      if (isDateType(getTableLeafType(gridSchema, tableColumnTypeMap, leafId, DELIMITER))) set.add(leafId);
    });
    return set;
  }, [leafOrder, gridSchema, tableColumnTypeMap]);

  // Column totals over the WHOLE in-memory row set — every row the fetch
  // returned, not the page on screen. A leaf is totalled when it is a metric
  // (never the identifier, attribute or detail columns) and numeric: declared
  // Number in the schema, or — for a raw table, whose columns carry no declared
  // types — holding nothing but numbers. Every leaf is read by its full path,
  // so a time-series `period§key` and `period§group§key` sum alike, as do the
  // flat data columns of a raw table. A leaf that is not totalled
  // has no entry; a numeric leaf with no values in these rows is null.
  // Which leaves get a total, decided from the schema alone — no row is read to
  // answer this. A merge table totals its metrics and never its identity or context
  // columns; a RAW table has no metrics at all, so its data columns ARE its values
  // and the same exclusion would (and did) discard every one of them, leaving a
  // "Totals" row of blanks on every raw and external grid. Its attribute and
  // table-attribute columns stay excluded — a Contract_ID or a Brokerage=0.07 is a
  // tag, not a quantity.
  const totalledLeaves = useMemo(() => {
    if (!gridSchema.show_column_totals) return [];
    const rawDataLeaves = tableType === 'raw'
      ? new Set((gridSchema?.detailColumns || []).map((col) => col.name))
      : null;
    return leafOrder.filter((leafId) => {
      if (!numberLeafSet.has(leafId)) return false;
      if (!staticLeafSet.has(leafId)) return true;
      return rawDataLeaves !== null && rawDataLeaves.has(leafId);
    });
  }, [gridSchema, leafOrder, staticLeafSet, numberLeafSet, tableType]);

  // Totals are summed a column at a time and painted as they land, because the pass
  // is O(columns x rows) and used to run synchronously in the render: a 50,000-row
  // three-year table froze the tab for several seconds after Apply, with the busy
  // overlay already gone, and again on every unrelated write to any table document.
  // A run is abandoned the moment the rows, the sort or the column set change, so a
  // new filter is never queued behind the old one's arithmetic.
  const [totalsByLeaf, setTotalsByLeaf] = useState(null);
  useEffect(() => {
    if (totalledLeaves.length === 0 || processedData.length === 0) {
      setTotalsByLeaf(null);
      return undefined;
    }

    // Drop the previous run's figures first. They belong to rows that are no longer
    // on screen, and leaving them up presented one dataset's totals under another's
    // rows — indefinitely, in a background tab where animation frames never fire.
    setTotalsByLeaf(null);

    let cancelled = false;
    let frame = null;
    const sums = {};
    // Splitting the leaf path once per column rather than once per cell: the old
    // code re-split it inside the row loop, 21.6M times on the table above.
    const paths = totalledLeaves.map((leafId) => leafId.split(DELIMITER));

    const sumChunk = (start) => {
      // Enough columns per frame that a narrow or short grid finishes in this one,
      // exactly as the synchronous version did, without blocking a wide one.
      const budget = Math.max(1, Math.ceil(CELLS_PER_TOTALS_CHUNK / Math.max(processedData.length, 1)));
      const end = Math.min(start + budget, totalledLeaves.length);
      for (let index = start; index < end; index += 1) {
        let sum = 0;
        let seen = 0;
        for (const row of processedData) {
          const value = getRawCellValueByPath(row, paths[index]);
          if (typeof value === 'number' && Number.isFinite(value)) {
            sum += value;
            seen += 1;
          }
        }
        sums[totalledLeaves[index]] = seen > 0 ? sum : null;
      }
      if (cancelled) return;
      setTotalsByLeaf({ ...sums });
      if (end < totalledLeaves.length) {
        frame = requestAnimationFrame(() => sumChunk(end));
      }
    };

    frame = requestAnimationFrame(() => sumChunk(0));
    return () => {
      cancelled = true;
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [processedData, totalledLeaves]);

  const totalledLeafSet = useMemo(() => new Set(totalledLeaves), [totalledLeaves]);
  // The label sits in the first column that is NOT itself totalled. On a raw table
  // any data column can be numeric, including the first, so the old "put it in cell
  // zero" rule would have written "Totals" over a figure.
  const totalsLabelLeaf = useMemo(
    () => leafOrder.find((leafId) => !totalledLeafSet.has(leafId)) ?? null,
    [leafOrder, totalledLeafSet],
  );
  // A raw table can be numeric in every column, leaving no cell free for the word.
  // The row then carries a tooltip instead of going unlabelled.
  const totalsRowTitle = totalsLabelLeaf === null ? 'Totals' : undefined;

  // The text of one totals-row cell, read by the row itself and by the column widths.
  const totalsCellText = useCallback((leafId) => {
    if (totalledLeafSet.has(leafId)) {
      // undefined while this column's sum is still being computed, null when the
      // column is numeric but these rows hold no number for it.
      const total = totalsByLeaf ? totalsByLeaf[leafId] : undefined;
      if (total === undefined) return '';
      return total === null ? '-' : formatNumber(total);
    }
    return leafId === totalsLabelLeaf ? 'Totals' : '';
  }, [totalsByLeaf, totalledLeafSet, totalsLabelLeaf]);

  const renderTotalsRow = () => {
    if (totalledLeaves.length === 0) return null;
    return (
      <TableRow hover={false} title={totalsRowTitle}>
        {leafOrder.map((leafId) => {
          const isTotalled = totalledLeafSet.has(leafId);
          return (
            <TableCell
              key={`totals-${leafId}`}
              align={isTotalled ? 'right' : 'left'}
              sx={{
                py: 1,
                px: 1,
                fontWeight: 700,
                borderTop: `2px solid ${theme.palette.divider}`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                position: 'sticky',
                bottom: 0,
                zIndex: 1150, // above body cells (incl. sticky-left at 100), below headers (1250)
                // Sticky cells need an opaque background; layer the old translucent
                // tint over solid paper.
                backgroundColor: theme.palette.background.paper,
                backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.06)})`,
              }}
            >
              {totalsCellText(leafId)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  const handleRequestSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(columnId);
      setOrder('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return processedData;

    const ascFactor = order === 'asc' ? 1 : -1;
    const keys = processedData.map(r => getRawCellValue(r, orderBy, DELIMITER));
    const idxs = processedData.map((_, i) => i);

    idxs.sort((ia, ib) => {
      const va = keys[ia], vb = keys[ib];

      const aNull = va === null || va === undefined || va === '';
      const bNull = vb === null || vb === undefined || vb === '';
      if (aNull && bNull) return processedData[ia]._internalIndex - processedData[ib]._internalIndex;
      if (aNull) return 1 * ascFactor;
      if (bNull) return -1 * ascFactor;

      if (typeof va === 'number' && typeof vb === 'number') {
        if (va === vb) return processedData[ia]._internalIndex - processedData[ib]._internalIndex;
        return (va - vb) * ascFactor;
      }

      const cmp = naturalCollator.compare(String(va), String(vb));
      if (cmp !== 0) return cmp * ascFactor;
      return processedData[ia]._internalIndex - processedData[ib]._internalIndex;
    });

    return idxs.map(i => processedData[i]);
  }, [processedData, orderBy, order, naturalCollator]);

  // Windowed scrolling: only ~WINDOW_SIZE rows are mounted, framed by two
  // spacer rows sized so the scrollbar reflects the full dataset.
  const {
    start: windowStart,
    end: windowEnd,
    topPad,
    bottomPad,
    attachContainer,
    measureRow,
    resetWindow,
  } = useVirtualWindow(sortedData.length, { defaultRowHeight: 37 });

  const windowRows = useMemo(() => {
    return sortedData.slice(windowStart, windowEnd);
  }, [sortedData, windowStart, windowEnd]);

  // New fetch or re-sort: back to the top, like the old pagination's page 0.
  useEffect(() => {
    resetWindow();
  }, [sortedData, resetWindow]);

  // Cell text is formatted straight from the row on every render. There is
  // deliberately no cross-render cache: formatting is microseconds per cell,
  // the memoised DataTableRow already skips retained rows on window shifts,
  // and a cache keyed by row position outlived the dataset it was built from
  // (a new query painted the previous query's values). Stable identity
  // (useCallback) so the memoised row's props stay referentially stable.
  const renderCellContent = useCallback((row, columnId) => {
      const value = getCellValue(row, columnId, dateLeafSet);
      // Render null/undefined as '-'; a boolean reads as the platform's Yes/No.
      if (value === null || value === undefined) return '-';
      if (typeof value === 'boolean') return formatBooleanValue(value);
      return String(value); // Ensure output is stringifiable for React
  }, [dateLeafSet]);

  // A click on a cell of lineageLeafSet opens the lineage drawer on it, as
  // CellLineageDrawer's `cellInfo` describes.
  const handleCellClick = useCallback((row, leafId) => {
      const currentValue = getCellValue(row, leafId, dateLeafSet);

      if (tableType === 'raw') {
          const contributingUnits = row[CONTRIBUTOR_MARKER_FIELD];
          if (!contributingUnits || contributingUnits.length === 0) return;
          setSelectedCell({ contributingUnits, column: leafId, currentValue });
          return;
      }

      const identifier = row[tableSchema.identifier.name];
      if (!identifier) return;

      // A detail or attribute cell is its column, with no period.
      if (staticLeafSet.has(leafId)) {
          setSelectedCell({ identifier, column: leafId, periodKey: null, period: null, currentValue });
          return;
      }

      // A metric cell's leafId is "2024_01§Gross_Premium", or "2024_01§Group§Subfield"
      // for a leaf inside a group: the period, then the leaf's path. A value carried
      // forward from an earlier period is explained by the period that reported it.
      const parts = leafId.split(DELIMITER);
      const period = parts[0];
      const sourcePeriod = currentValue === null ? null : forwardFilledFrom?.[String(identifier)]?.[period];
      setSelectedCell({
          identifier,
          column: parts.slice(1).join('.'),
          period,
          sourcePeriod,
          periodKey: sourcePeriod ?? period,
          currentValue,
      });
  }, [tableType, staticLeafSet, tableSchema, dateLeafSet, forwardFilledFrom]);

  // The body's row and cell styles, and each header cell's look, shared with every data
  // preview (tableRendering.js).
  const bodyStyles = useMemo(() => gridBodyStyles(theme), [theme]);
  const headerCellBase = useMemo(() => gridHeaderCellStyles(theme), [theme]);

  // Every column's fixed width, sized once per fetch from a sample of the fetched rows (in
  // fetched order, so sorting never changes a width), the header labels and the totals.
  const columnWidths = useColumnWidths({
    rows: processedData,
    leafOrder,
    headerRows,
    getCellText: renderCellContent,
    headerText: headerTextAsDrawn,
    headerIconWidth: SORT_ICON_WIDTH,
    totalsText: totalledLeaves.length > 0 ? totalsCellText : null,
  });


  const runLocalExport = () => {
    const exportData = [];

    // Optional: prepend custom header rows (key/value pairs) before table header
    if (exportHeaders && typeof exportHeaders === 'object') {
      const bannedKeys = new Set(['startDate', 'endDate', 'start_date', 'end_date']);
      Object.entries(exportHeaders).forEach(([key, val]) => {
        if (bannedKeys.has(key)) return;
        let outVal = val;
        if (Array.isArray(outVal)) {
          outVal = outVal.join(', ');
        } else if (outVal === null || outVal === undefined) {
          outVal = '';
        } else if (typeof outVal === 'object') {
          outVal = JSON.stringify(outVal);
        }
        exportData.push([formatText(key), outVal]);
      });
    }

    // Build complex header rows for Excel (handling merged cells is tricky in xlsx library, simplified approach below)
    // Option 1: Simplified Header (Just Leaf Headers)
    const headerRow = leafOrder.map(leafId => {
        // Create a more readable header string by joining parts
        return leafId.split(DELIMITER).map(formatText).join(' > ');
    });
    exportData.push(headerRow);

    /* // Option 2: Attempt at Multi-Row Header (More Complex, requires careful span calculation)
       // This is a basic representation and might need refinement for complex spans.
       const excelHeaderRows = [];
       if (headerRows.top.length > 0) excelHeaderRows.push(headerRows.top.map(h => h.label));
       if (headerRows.middle.length > 0) {
           const middleRow = [];
           // Pad beginning for static columns based on maxDepth
           const staticCount = headerRows.top.filter(h => h.isStatic).length;
           if (maxDepth === 2) middleRow.push(...Array(staticCount).fill(''));
           middleRow.push(...headerRows.middle.map(h => h.label));
           excelHeaderRows.push(middleRow);
       }
       if (headerRows.bottom.length > 0) {
           const bottomRow = [];
           const staticCount = headerRows.top.filter(h => h.isStatic).length;
           // Pad according to maxDepth
           if (maxDepth >= 1) bottomRow.push(...Array(staticCount).fill(''));
           bottomRow.push(...headerRows.bottom.map(h => h.label));
           excelHeaderRows.push(bottomRow);
       }
       exportData.push(...excelHeaderRows);
    */

    // Add data rows — in the order the grid shows them: "As-Is" is the grid.
    // The leaf path and its declared type are resolved once per COLUMN; the row loop
    // used to re-split the id and re-walk the schema for every cell.
    const columnPaths = leafOrder.map(leafId => leafId.split(DELIMITER));
    const columnTypes = leafOrder.map(leafId => getTableLeafType(
      tableSchema, tableColumnTypeMap, leafId, DELIMITER));
    sortedData.forEach(row => {
      exportData.push(columnPaths.map((path, index) => (
        toExcelCellValue(getRawCellValueByPath(row, path), columnTypes[index])
      )));
    });

    // Create worksheet, sized so its values are legible when it first opens
    const ws = applyColumnWidths(XLSX.utils.aoa_to_sheet(exportData, { UTC: true, dateNF: 'dd/mm/yyyy' }), exportData);

    // TODO: Add cell merging info to 'ws['!merges']' if using Option 2 header approach

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');

    // Export file
    XLSX.writeFile(wb, `${tableSchema?.name || 'data-export'}.xlsx`);
  };

  // The whole grid, exported on the server. Above the in-browser ceiling this is the
  // only path that produces a file at all, and the server has to be told the columns:
  // a time-series grid's period columns come from the data, and display_columns and
  // display_order have already been applied to build leafOrder.
  const runRemoteExport = async () => {
    const payload = {
      table_id: exportContext?.tableId,
      filters: exportContext?.filters || {},
      range_filters: exportContext?.rangeFilters || {},
      as_is: {
        columns: leafOrder.map(leafId => ({
          leaf_id: leafId,
          label: leafId.split(DELIMITER).map(formatText).join(' > '),
          type: getTableLeafType(tableSchema, tableColumnTypeMap, leafId, DELIMITER),
        })),
        headers: exportHeaders && typeof exportHeaders === 'object' ? exportHeaders : {},
      },
    };
    await downloadFromEndpoint(
      functionEndpoint('export_spreadsheet_data'),
      { method: 'POST', body: JSON.stringify(payload) },
      `${tableSchema?.name || 'data-export'}`,
    );
  };

  const handleExportExcel = async () => {
    if (!processedData || processedData.length === 0) return;
    setExporting(true);
    setExportError('');
    setExportNotice('');

    // SheetJS holds the whole workbook in memory — one cell object per cell, then the
    // XML on top — so a grid at the 50,000-row Inspect cap froze the tab for minutes
    // or had the renderer killed, which produced no file and no message (try/catch
    // cannot see an out-of-memory kill). Past the ceiling the server builds it.
    if (leafOrder.length * sortedData.length > MAX_IN_BROWSER_EXPORT_CELLS) {
      try {
        await runRemoteExport();
        if (orderBy) {
          setExportNotice('Exported in the table\'s default row order — a grid this large '
                          + 'is prepared on the server, which does not apply the column sort.');
        }
      } catch (e) {
        console.error('Export failed:', e);
        // The server's own sentence when it has one — "No rows matched these filters",
        // "This view is too large to export in one file" — is what the user can act on.
        setExportError(serverGuidanceOr(e, 'Export failed. Try again; if it fails again, contact support.'));
      } finally {
        setExporting(false);
      }
      return;
    }

    // The XLSX serialization is synchronous and blocks the main thread — defer
    // it a tick so the busy overlay gets a paint first.
    setTimeout(() => {
      try {
        runLocalExport();
      } catch (e) {
        console.error('Export failed:', e);
        setExportError('Export failed. Try again; if it fails again, contact support.');
      } finally {
        setExporting(false);
      }
    }, 50);
  };

  const openMenu = (e) => setMenuAnchorEl(e.currentTarget);
  const closeMenu = () => setMenuAnchorEl(null);

  const triggerTemplateExport = async (export_format_id) => {
    setExporting(true);
    setExportError('');
    setExportNotice('');
    try {
      const url = functionEndpoint('export_spreadsheet_data');
      const payload = {
        table_id: exportContext?.tableId,
        filters: exportContext?.filters || {},
        range_filters: exportContext?.rangeFilters || {},
        ...(export_format_id ? { export_format_id } : {})
      };
      await downloadFromEndpoint(url, { method: 'POST', body: JSON.stringify(payload) }, `${exportContext?.tableName || 'export'}`);
    } catch (e) {
      console.error('Template export failed:', e);
      setExportError('Export failed. Check the mapping in Table Settings › Export; if it still fails, contact support.');
    } finally {
      setExporting(false);
    }
  };


  // Busy = the parent is fetching table data (Apply Filters) or an export is
  // in flight. The overlay greys out the table and blocks interaction.
  const busy = Boolean(loading) || exporting;

  // Rendered in BOTH footers. An Alert, not a one-line caption: the server's refusal
  // explains which dimension to reduce, and a noWrap caption clipped that to an
  // ellipsis with the advice reachable only by hovering. Living in the collapsed
  // footer alone meant an expanded grid showed no explanation at all.
  const exportMessage = () => {
    if (!exportError && !exportNotice) return null;
    return (
      <Alert
        severity={exportError ? 'error' : 'info'}
        onClose={() => { setExportError(''); setExportNotice(''); }}
        sx={{ ml: 1, py: 0, minWidth: 0, flexShrink: 1 }}
      >
        {exportError || exportNotice}
      </Alert>
    );
  };

  const busyOverlay = (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 1300, // above the sticky table headers (1250)
        backgroundColor: alpha(theme.palette.background.paper, 0.6),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress />
    </Box>
  );

  if (!processedData || processedData.length === 0) {
    return (
      <Box sx={{ position: 'relative', height: '100%', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {busy && busyOverlay}
        <Typography variant="body1" color="text.secondary" align="center">
          {fetched
            ? 'No data available. Try adjusting filters or selecting a table.'
            : 'Apply filters to load your data.'}
        </Typography>
      </Box>
    );
  }

  const headerRowHeight = 44;

  const columnGroup = <ColumnWidthGroup leafOrder={leafOrder} columnWidths={columnWidths} />;

  const getHeaderStickyTopPosition = (level) => {
    if (level === 'top')    return 0;
    if (level === 'middle') return headerRowHeight;
    if (level === 'bottom') return maxDepth === 2
                                 ? headerRowHeight * 2
                                 : headerRowHeight;
    return 0;
  };


  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {busy && busyOverlay}
      {!isExpanded && (
      <TableContainer ref={attachContainer} sx={{
        flexGrow: 1,
        overflow: 'auto', // Ensure container scrolls
        // Custom scrollbar styling from original
        '&::-webkit-scrollbar': { width: '0.5em', height: '0.5em' },
        '&::-webkit-scrollbar-track': { background: alpha(theme.palette.primary.main, 0.05), borderRadius: '12px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.primary.main, 0.2), borderRadius: '12px', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.3) } },
      }}>
        <Table stickyHeader aria-label="data table" size="medium" sx={{ tableLayout: 'fixed' }}>
          {columnGroup}
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1250 }}>
            {/* Top Header Row */}
            {headerRows.top.length > 0 && (
              <TableRow>
                {headerRows.top.map((header) => {
                  const isSortable = header.isStatic || (header.depth === 0 && header.colSpan === 1);
                  const isActive = orderBy === header.id;
                  return (
                    <TableCell
                      key={header.id}
                      title={header.label}
                      rowSpan={header.rowSpan}
                      colSpan={header.colSpan}
                      align={header.isStatic ? 'left' : 'center'} // Align static left, dynamic center
                      sx={{
                        ...headerCellBase,
                        position: 'sticky', // All header cells are sticky
                        top: getHeaderStickyTopPosition('top'),
                        left: header.isSticky ? 0 : 'auto', // Only row identifier is sticky left
                        zIndex: header.isSticky ? 1201 : (header.isStatic ? 1150 : 1100), // Ensure row identifier is topmost
                        textTransform: header.isStatic ? 'capitalize' : 'none',
                        cursor: isSortable ? 'pointer' : 'default',
                      }}
                    >
                      {isSortable ? (
                        <TableSortLabel
                          active={isActive}
                          direction={isActive ? order : 'asc'}
                          onClick={() => handleRequestSort(header.id)}
                        >
                          {header.label}
                        </TableSortLabel>
                      ) : (
                        header.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Middle Header Row (if maxDepth=2) */}
            {maxDepth === 2 && headerRows.middle.length > 0 && (
              <TableRow>
                {/* Details don't render cells here due to rowSpan */}
                {headerRows.middle.map((header) => (
                  <TableCell
                    key={header.id}
                    title={header.label}
                    colSpan={header.colSpan}
                    rowSpan={header.rowSpan || 1}
                    align="center"
                    sx={{
                      ...headerCellBase,
                      position: 'sticky',
                      top: getHeaderStickyTopPosition('middle'), // Sticky below the first row
                      zIndex: 1100,
                      cursor: header.isLeaf ? 'pointer' : 'default',
                    }}
                  >
                    {header.isLeaf ? (
                      <TableSortLabel
                        active={orderBy === header.id}
                        direction={orderBy === header.id ? order : 'asc'}
                        onClick={() => handleRequestSort(header.id)}
                      >
                        {header.label}
                      </TableSortLabel>
                    ) : (
                      header.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            )}

            {/* Bottom Header Row (Leaf Headers for Dynamic Columns) */}
            {maxDepth > 0 && headerRows.bottom.length > 0 && (
              <TableRow>
                 {/* Details don't render cells here due to rowSpan */}
                {headerRows.bottom.map((header) => {
                  const isActive = orderBy === header.id;
                  return (
                    <TableCell
                      key={header.id}
                      title={header.label}
                      align="center" // Align leaf dynamic headers center/right maybe?
                      sx={{
                        ...headerCellBase,
                        position: 'sticky',
                        top: getHeaderStickyTopPosition('bottom'), // Sticky below previous rows
                        zIndex: 1100,
                        cursor: 'pointer',
                      }}
                    >
                      <TableSortLabel
                        active={isActive}
                        direction={isActive ? order : 'asc'}
                        onClick={() => handleRequestSort(header.id)}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableHead>
          <TableBody sx={bodyStyles}>
            {topPad > 0 && (
              <TableRow aria-hidden="true">
                <TableCell colSpan={leafOrder.length} padding="none" sx={{ border: 'none' }} style={{ height: topPad }} />
              </TableRow>
            )}
            {windowRows.map((row, windowIndex) => (
              <DataTableRow
                key={row._internalIndex} // Use the internal index as key
                row={row}
                globalIndex={windowStart + windowIndex}
                rowRef={windowIndex === 0 ? measureRow : undefined}
                leafOrder={leafOrder}
                stickyId={stickyId}
                staticLeafSet={staticLeafSet}
                numberLeafSet={numberLeafSet}
                lineageLeafSet={lineageLeafSet}
                onCellClick={handleCellClick}
                getCell={renderCellContent}
              />
            ))}
            {bottomPad > 0 && (
              <TableRow aria-hidden="true">
                <TableCell colSpan={leafOrder.length} padding="none" sx={{ border: 'none' }} style={{ height: bottomPad }} />
              </TableRow>
            )}
            {/* Totals Row (optional) — sticky so it stays visible while scrolling */}
            {renderTotalsRow()}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      {/* Footer with Pagination and Export */}
      {!isExpanded && (
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: `1px solid ${theme.palette.divider}`,
        py: 0.5, // Reduced padding
        px: 1,
        backgroundColor: theme.palette.background.paper, // Match header background
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Tooltip title="Download">
            <IconButton onClick={openMenu} size="small">
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={closeMenu}
          >
            <MenuItem onClick={() => { closeMenu(); handleExportExcel(); }}>
              <ListItemText primary="Export As-Is" />
            </MenuItem>
            {exportMappings.status === 'loading' && (
              <MenuItem disabled>
                <ListItemText primary="Loading saved formats…" />
              </MenuItem>
            )}
            {exportMappings.status === 'error' && (
              <MenuItem disabled>
                <ListItemText primary="Failed to load saved formats" />
              </MenuItem>
            )}
            {exportMappings.list.map(m => (
              <MenuItem key={m.id} onClick={() => { closeMenu(); triggerTemplateExport(m.id); }}>
                <ListItemText primary={m.name || m.id} />
              </MenuItem>
            ))}
          </Menu>
          {exportMessage()}
        </Box>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', pr: 1 }}>
          {countNoun(processedData.length, 'row')}
        </Typography>
      </Box>
      )}

      <Dialog open={isExpanded} onClose={() => setIsExpanded(false)} fullWidth maxWidth={false} transitionDuration={0} TransitionProps={{ timeout: 0 }} PaperProps={{ sx: { width: '95vw', height: '90vh' } }}>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {busy && busyOverlay}
            <IconButton onClick={() => setIsExpanded(false)} size="small" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2000 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <TableContainer ref={attachContainer} sx={{
              flexGrow: 1,
              overflow: 'auto',
              '&::-webkit-scrollbar': { width: '0.5em', height: '0.5em' },
              '&::-webkit-scrollbar-track': { background: alpha(theme.palette.primary.main, 0.05), borderRadius: '12px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.primary.main, 0.2), borderRadius: '12px', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.3) } },
            }}>
              <Table stickyHeader aria-label="data table" size="medium" sx={{ tableLayout: 'fixed' }}>
                {columnGroup}
                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1250 }}>
                  {headerRows.top.length > 0 && (
                    <TableRow>
                      {headerRows.top.map((header) => {
                        const isSortable = header.isStatic || (header.depth === 0 && header.colSpan === 1);
                        const isActive = orderBy === header.id;
                        return (
                          <TableCell
                            key={header.id}
                            title={header.label}
                            rowSpan={header.rowSpan}
                            colSpan={header.colSpan}
                            align={header.isStatic ? 'left' : 'center'}
                            sx={{
                              ...headerCellBase,
                              position: 'sticky',
                              top: getHeaderStickyTopPosition('top'),
                              left: header.isSticky ? 0 : 'auto',
                              zIndex: header.isSticky ? 1201 : (header.isStatic ? 1150 : 1100),
                              textTransform: header.isStatic ? 'capitalize' : 'none',
                              cursor: isSortable ? 'pointer' : 'default',
                            }}
                          >
                            {isSortable ? (
                              <TableSortLabel
                                active={isActive}
                                direction={isActive ? order : 'asc'}
                                onClick={() => handleRequestSort(header.id)}
                              >
                                {header.label}
                              </TableSortLabel>
                            ) : (
                              header.label
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )}

                  {maxDepth === 2 && headerRows.middle.length > 0 && (
                    <TableRow>
                      {headerRows.middle.map((header) => (
                        <TableCell
                          key={header.id}
                          title={header.label}
                          colSpan={header.colSpan}
                          rowSpan={header.rowSpan || 1}
                          align="center"
                          sx={{
                            ...headerCellBase,
                            position: 'sticky',
                            top: getHeaderStickyTopPosition('middle'),
                            zIndex: 1100,
                            cursor: header.isLeaf ? 'pointer' : 'default',
                          }}
                        >
                          {header.isLeaf ? (
                            <TableSortLabel
                              active={orderBy === header.id}
                              direction={orderBy === header.id ? order : 'asc'}
                              onClick={() => handleRequestSort(header.id)}
                            >
                              {header.label}
                            </TableSortLabel>
                          ) : (
                            header.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}

                  {maxDepth > 0 && headerRows.bottom.length > 0 && (
                    <TableRow>
                      {headerRows.bottom.map((header) => {
                        const isActive = orderBy === header.id;
                        return (
                          <TableCell
                            key={header.id}
                            title={header.label}
                            align="center"
                            sx={{
                              ...headerCellBase,
                              position: 'sticky',
                              top: getHeaderStickyTopPosition('bottom'),
                              zIndex: 1100,
                              cursor: 'pointer',
                            }}
                          >
                            <TableSortLabel
                              active={isActive}
                              direction={isActive ? order : 'asc'}
                              onClick={() => handleRequestSort(header.id)}
                            >
                              {header.label}
                            </TableSortLabel>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )}
                </TableHead>
                <TableBody sx={bodyStyles}>
                  {topPad > 0 && (
                    <TableRow aria-hidden="true">
                      <TableCell colSpan={leafOrder.length} padding="none" sx={{ border: 'none' }} style={{ height: topPad }} />
                    </TableRow>
                  )}
                  {windowRows.map((row, windowIndex) => (
                    <DataTableRow
                      key={row._internalIndex}
                      row={row}
                      globalIndex={windowStart + windowIndex}
                      rowRef={windowIndex === 0 ? measureRow : undefined}
                      leafOrder={leafOrder}
                      stickyId={stickyId}
                      staticLeafSet={staticLeafSet}
                      numberLeafSet={numberLeafSet}
                      lineageLeafSet={lineageLeafSet}
                      onCellClick={handleCellClick}
                      getCell={renderCellContent}
                    />
                  ))}
                  {bottomPad > 0 && (
                    <TableRow aria-hidden="true">
                      <TableCell colSpan={leafOrder.length} padding="none" sx={{ border: 'none' }} style={{ height: bottomPad }} />
                    </TableRow>
                  )}
                  {renderTotalsRow()}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: `1px solid ${theme.palette.divider}`,
              py: 0.5,
              px: 1,
              backgroundColor: theme.palette.background.paper,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Tooltip title="Export to Excel">
                  <IconButton onClick={handleExportExcel} size="small">
                    <FileDownloadIcon fontSize="small"/>
                  </IconButton>
                </Tooltip>
                {exportMessage()}
              </Box>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', pr: 1 }}>
                {countNoun(processedData.length, 'row')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Cell Lineage Drawer */}
      {selectedCell && companyId && tableId && (
          <CellLineageDrawer
              open={!!selectedCell}
              onClose={() => setSelectedCell(null)}
              companyId={companyId}
              tableId={tableId}
              tableSchema={tableSchema}
              cellInfo={selectedCell}
          />
      )}
    </Box>
  );
}

export default DataTable;
